import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Flex, Spacer, VisuallyHidden } from '@zillow/constellation';
import { Button, Heading, Paragraph, Popover, TextButton } from '@zillow/constellation-10';

import { useIsMutating } from '@zg-rentals/react-query5';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { PaymentGatewayContext } from '../../../components/PaymentGateway/context';

import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import useObserveVisible from '../../../hooks/useObserveVisible';
import useAccessActivateOnReview from '../../../hooks/useAccessActivateOnReview';

import actions from '../../../containers/CreateListingPage/actions';

import accessUtils from '../../../utils/accessUtils';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import { ACTIVATE_LISTING_MUTATION_KEY } from '../../../constants/QueryConstants';

import ReviewPageCSEvents from '../../../model/ClickstreamEvent/ReviewPageCSEvents';

import PageWizard from '../../../components/Wizard/PageWizard';
import PaymentGateway from '../../../components/PaymentGateway';
import PreActivationModalWrapper from '../../../components/PreActivationModalWrapper';
import PropertiesActions from '../../../actions/PropertiesActions';
import SFAccordionContainer from '../../../components/SFAccordionContainer';
import SaveAndExitModal from '../../../components/Modal/SaveAndExitModal';
import ListingPreviewModal from '../../../components/Modal/ListingPreviewModal';
import SFTotalMonthlyPriceWithFeeAcknowledgementModal from '../../Modal/SFTotalMonthlyPriceWithFeeAcknowledgementModal';
import ErrorModal from '../../../components/Modal/ErrorModal';
import WizardAlert from '../../../components/WizardAlert';
import ListingActivationTerms from '../../../components/ListingActivationTerms';
import ConnectedListingFooter from '../../../components/ConnectedListingFooter';
import PropertyDetailsHOC from '../../../components/PropertyDetailsHOC';
import Banners from '../../../containers/PropertyDetailsContainer/components/Banners';
import DuplicateListingDetails from '../../../components/DuplicateListingDetails';
import ListingHeader from '../../../components/postingPath/ListingHeader';
import ListingStats from '../../../components/postingPath/ListingStats';
import ListingPostedForSaleBanner from '../../../components/postingPath/ListingPostedForSaleBanner';
import ContentSectionEditButton from '../../../components/WizardModularListing/Review/components/ContentSectionEditButton';

import PropertyCard from './components/PropertyCard';

import { StyledComponent, StyledPreviewListingCTAFlex } from './StyledComponents';

import type { ReactNode } from 'react';
import type { BreakpointToken } from '@/styled-system/tokens';

const BUTTON_FIELD_NAME = 'preview-listing-button';
const PREVIEW_LISTING_BUTTON_TEXT = 'Preview listing';
const PREVIEW_BUTTON_DISABLED_MESSAGE = 'All the required fields must be completed.';
const noOp = () => {};

type Props = {
  onGoToPrevStep: () => void | null;
  onGoToNextStep: () => void | null;
  onExitWizard: (...args: Array<unknown>) => void;
  wizardTitle: ReactNode;
  progressStepper: ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: BreakpointToken;
};

const Review = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const {
    modularListingState: { displaySaveAndExitModal, serverErrorObj, isInReactivationFlow },
    reducerDispatch,
  } = useContext(ModularListingContext);
  const {
    pgState: { isResponseSuccess },
  } = useContext(PaymentGatewayContext);

  const {
    active,
    activeProductType,
    appConfig,
    appConfigCanAccessTotalMonthlyPriceWithFee,
    canAccessRentalProtectionFromML,
    canBeRelisted,
    cookieEnabledServices,
    currentProperty,
    displayTotalMonthlyPriceWithFee,
    feedName,
    forSaleExists,
    forSaleUrlOnZillow,
    isEnterpriseCustomer,
    isFeed,
    isFreemiumEnabled,
    isMultifamily,
    isRentalProtectionEnabled,
    isTotalMonthlyPriceWithFeeEligible,
    isUpdateActiveStatusAllowed,
    recommendations,
    singleFamilyContract,
    topDupesDetails,
    user,
  } = useSelector(
    (state) => ({
      active: state.properties.currentProperty.active,
      activeProductType: state.properties.currentProperty.freemium.activeProduct.type,
      appConfig: state.app.appConfig,
      appConfigCanAccessTotalMonthlyPriceWithFee: state.app.appConfig.config.features.canAccessTotalMonthlyPriceWithFee,
      canAccessRentalProtectionFromML: state.app.canAccessRentalProtectionFromML,
      canBeRelisted: state.properties.currentProperty.canBeRelisted,
      cookieEnabledServices: state.app.cookieEnabledServices,
      currentProperty: state.properties.currentProperty,
      displayTotalMonthlyPriceWithFee: state.properties.currentProperty.displayTotalMonthlyPriceWithFee,
      feedName: state.properties.currentProperty.feedName,
      forSaleExists: state.properties.currentProperty.forSaleExists,
      forSaleUrlOnZillow: state.properties.currentProperty.forSaleUrlOnZillow,
      isEnterpriseCustomer: state.user.isEnterpriseCustomer,
      isFeed: state.properties.currentProperty.isFeed,
      isFreemiumEnabled: state.properties.currentProperty.freemium.isEnabled,
      isMultifamily: state.properties.currentProperty.isMultifamily,
      isRentalProtectionEnabled: state.properties.currentProperty.rentalProtection.isEnabled,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
      isUpdateActiveStatusAllowed: state.properties.currentProperty.updateActiveStatus.isAllowed,
      recommendations: state.properties.recommendations,
      singleFamilyContract: state.properties.currentProperty.singleFamilyContract,
      topDupesDetails: state.properties.currentProperty.topDupesDetails,
      user: state.user,
    }),
    shallowEqual,
  );
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));

  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const canAccessMLReactivationFlow = accessUtils.canAccessMLReactivationFlow({
    currentProperty,
  });

  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({
    canBeRelisted,
  });

  const canAccessListingPreview = accessUtils.canAccessListingPreview({
    appConfig,
    cookieEnabledServices,
    currentProperty,
  });

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    canAccessTotalMonthlyPriceWithFee: appConfigCanAccessTotalMonthlyPriceWithFee,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const { canAccessActivateOnReview } = useAccessActivateOnReview();

  const canPostForSaleListing = accessUtils.canPostForSaleListing({ appConfig });
  const showListingPostedForSaleBanner = !active && !isFeed && forSaleExists && canPostForSaleListing;

  const [canPublish, setCanPublish] = useState(false);
  const [displayPaymentGateway, setDisplayPaymentGateway] = useState(false);
  const [displayListingPreviewModal, setDisplayListingPreviewModal] = useState(false);
  const [
    displaySFTotalMonthlyPriceWithFeeAcknowledgementModal,
    setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal,
  ] = useState(false);
  const [showPreActivationModal, setShowPreActivationModal] = useState(false);
  const [activateListingError, setActivateListingError] = useState(null);

  const ctaRef = useRef<HTMLButtonElement>(null);
  const shouldAutoActivateRef = useRef(false);

  const [isVisible, isVisibleRef] = useObserveVisible({
    bypassBoundingClientRect: true,
  });

  const isActivateListingPending = useIsMutating({ mutationKey: ACTIVATE_LISTING_MUTATION_KEY }) > 0;

  useEffect(() => {}, [displaySaveAndExitModal, isRentalProtectionEnabled]);

  useEffect(() => {
    if (displayListingPreviewModal) {
      ReviewPageCSEvents.PreviewListing({ currentProperty, user });
    }
  }, [displayListingPreviewModal]);

  useEffect(() => {
    if (recommendations?.isEnabled) {
      const recsShown: Record<string, unknown> = {};

      recsShown.listingAlias = currentProperty.listingAlias;
      recommendations?.recommendations?.forEach((rec: Record<string, unknown>) => {
        recsShown[rec.type as string] = !rec.isComplete;
      });
    }
  }, [recommendations]);

  useEffect(() => {
    //  If listing was upgraded but not activated yet, stay on review step
    if (isResponseSuccess && active) {
      onGoToNextStep();
    }
  }, [isResponseSuccess, active]);

  const canFeedConnectSwitchSource = accessUtils.canFeedConnectSwitchSource({ user });

  // listing is eligible for premium but has not been upgraded - need to show upsell
  const showPremiumUpsell = isFreemiumEnabled && !isActiveProductTypePremium;

  const hasNextStep = useMemo(() => {
    return showPremiumUpsell;
  }, [showPremiumUpsell]);
  const nextLabel = hasNextStep ? (canAccessActivateOnReview ? 'Activate listing' : 'Next') : 'Publish';

  const isNotActiveAndUpdateActiveStatusNotAllowed = !active && !isUpdateActiveStatusAllowed;
  const isNextDisabled = !canPublish || isNotActiveAndUpdateActiveStatusNotAllowed;

  const showDupeDetails = !!topDupesDetails?.dupes?.length;

  const previewListingCTA = useMemo(() => {
    return canAccessListingPreview && isUpdateActiveStatusAllowed ? (
      <StyledPreviewListingCTAFlex display="flex" flex="1 0 auto" justifyContent="flex-end" alignItems="center">
        {isNextDisabled ? (
          <Popover
            header={<Heading level={4}>Listing preview is disabled</Heading>}
            body={<Paragraph>{PREVIEW_BUTTON_DISABLED_MESSAGE}</Paragraph>}
            tone="neutral"
            trigger={
              <Button
                tone="neutral"
                emphasis="outlined"
                fluid={true}
                aria-label={`disabled-edit-button-for-${BUTTON_FIELD_NAME}`}
              >
                {PREVIEW_LISTING_BUTTON_TEXT}
              </Button>
            }
          />
        ) : (
          <Button
            data-testid={BUTTON_FIELD_NAME}
            onClick={() => setDisplayListingPreviewModal(true)}
            tone="neutral"
            emphasis="outlined"
            fluid={true}
            data-eventid={ReviewPageCSEvents.PreviewListing.data_event_id}
          >
            {PREVIEW_LISTING_BUTTON_TEXT}
          </Button>
        )}
      </StyledPreviewListingCTAFlex>
    ) : undefined;
  }, [canAccessListingPreview, isUpdateActiveStatusAllowed, isNextDisabled]);

  const updateDisplayPaymentGateway = (value: boolean) => {
    setDisplayPaymentGateway(value);
  };

  const handleChargeAndActivateListingResponse = (chargeAndActivateListingResponse: {
    response: { activation: { daysExtended: number; expirationDateTimestamp: number } };
    success: boolean;
  }) => {
    const {
      response: {
        activation: { daysExtended, expirationDateTimestamp },
      },
      success,
    } = chargeAndActivateListingResponse;

    if (success) {
      dispatch(
        PropertiesActions.updateCurrentProperty({
          active: true,
          daysExtended,
          expirationDateTimestamp,
          singleFamilyContract: {
            ...singleFamilyContract,
            isActive: true,
          },
        }),
      );
      onGoToNextStep();
    }
  };

  const handleNextClick = ({ shouldAutoActivate = false, shouldTriggerDeactivate = false } = {}) => {
    if (canAccessActivateOnReview) {
      shouldAutoActivate = true;
    }

    if (ctaRef.current && shouldTriggerDeactivate) {
      ctaRef.current?.click();
      return;
    }

    if (singleFamilyContract.isRequired || isFeed) {
      setShowPreActivationModal(true);
      return;
    }

    if (
      canAccessRentalProtectionFromML &&
      canAccessMLReactivationFlow &&
      !isRentalProtectionEnabled &&
      !canAccessActivateOnReview
    ) {
      onGoToNextStep();
      return;
    }

    if (canAccessTotalMonthlyPriceWithFee && !displayTotalMonthlyPriceWithFee) {
      shouldAutoActivateRef.current = shouldAutoActivate;
      setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal(true);
      return;
    }

    /**
     * NOTE: since `SFTotalMonthlyPriceWithFeeAcknowledgementModal` is intercepting
     * should continue the logic on `Confirm`
     **/
    if (!isFreemiumEnabled || shouldAutoActivate || isActiveProductTypePremium) {
      autoActivate();
      return;
    }

    if (isFreemiumEnabled) {
      onGoToNextStep();
      return;
    }

    onGoToNextStep();
  };

  const handleSaveAndExitModalCancel = () => {
    actions.updateDisplaySaveAndExitModal({ reducerDispatch, displaySaveAndExitModal: false });
    //  todo
    if (hasNextStep && !isNextDisabled) {
      handleNextClick();

      return;
    }
  };

  const handleSaveAndExitModalSubmit = () => {
    actions.updateHasConfirmedSaveAndExit({ reducerDispatch, hasConfirmedSaveAndExit: true });

    setTimeout(() => {
      actions.updateDisplaySaveAndExitModal({ reducerDispatch, displaySaveAndExitModal: false });
    }, 300);
  };

  const handleSaveAndExitModalClose = () => {
    actions.updateDisplaySaveAndExitModal({ reducerDispatch, displaySaveAndExitModal: false });
  };

  const autoActivate = () => {
    setDisplayPaymentGateway(true);
  };

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={handleNextClick}
      onBackClick={onGoToPrevStep}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint as unknown as string}
      nextLabel={nextLabel}
      isNextLoading={isActivateListingPending}
      hideStepIndex={isInReactivationFlow}
      hideBackButton={isInReactivationFlow && !canAccessRelistingFlow}
      footer={
        isInReactivationFlow &&
        !canAccessRelistingFlow && (
          <ConnectedListingFooter
            ctaRef={ctaRef}
            onPublishClick={handleNextClick}
            isPublishedDisabled={!canPublish}
            isPublishLoading={isActivateListingPending}
          />
        )
      }
      isNextDisabled={isNextDisabled}
    >
      <StyledComponent data-testid="Review">
        <VisuallyHidden>
          <h1>Review Page</h1>
        </VisuallyHidden>

        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />
        <Spacer ref={isVisibleRef} marginBottom="lg">
          <PropertyCard />
        </Spacer>

        {isInReactivationFlow && <Banners />}
        {showListingPostedForSaleBanner && <ListingPostedForSaleBanner forSaleUrlOnZillow={forSaleUrlOnZillow} />}
        {showDupeDetails && !canFeedConnectSwitchSource && <DuplicateListingDetails />}
        {isFeed && <ListingHeader title={`Source: ${feedName}`} />}
        {!isFeed && canFeedConnectSwitchSource && <ListingHeader />}
        {isEnterpriseCustomer && <ListingStats />}

        <SFAccordionContainer setCanPublish={setCanPublish} previewListingCTA={previewListingCTA} />

        <Spacer marginTop="sm" />
        <ListingActivationTerms
          isTextCenter={true}
          actionText={`By ${active ? 'deactivating' : 'publishing'} this listing`}
          previewListingCTA={!isScreenMobile && previewListingCTA && !isVisible ? previewListingCTA : undefined}
        />

        {Boolean(isScreenMobile && previewListingCTA && !isVisible) && (
          <Flex display="flex" justifyContent="center" marginTop="md">
            {isNextDisabled ? (
              <ContentSectionEditButton
                isDisabled={isNextDisabled}
                tone="subtle"
                buttonFieldName={BUTTON_FIELD_NAME}
                editButtonText={PREVIEW_LISTING_BUTTON_TEXT}
                disabledMessage={PREVIEW_BUTTON_DISABLED_MESSAGE}
                onEditClick={noOp}
              />
            ) : (
              <TextButton
                data-testid={BUTTON_FIELD_NAME}
                disabled={isNextDisabled}
                onClick={() => setDisplayListingPreviewModal(true)}
              >
                {PREVIEW_LISTING_BUTTON_TEXT}
              </TextButton>
            )}
          </Flex>
        )}
      </StyledComponent>

      {isInReactivationFlow && <PropertyDetailsHOC isInvokedFromSF={!isMultifamily} />}

      {/**
       *  TODO: refactor to use PaymentGateway in containers/App
       *  example on how to handle in PayAndPublish
       */}
      <PaymentGateway
        // @ts-expect-error
        displayPaymentGateway={displayPaymentGateway}
        updateDisplayPaymentGateway={({ displayPaymentGateway }: { displayPaymentGateway: boolean }) => {
          updateDisplayPaymentGateway(displayPaymentGateway);
        }}
        onChargeAndActivateListingResponse={handleChargeAndActivateListingResponse}
      />
      <PreActivationModalWrapper
        showPreActivationModal={showPreActivationModal}
        updateShowPreActivationModal={setShowPreActivationModal}
        onChargeAndActivateListingResponse={handleChargeAndActivateListingResponse}
      />

      <SaveAndExitModal
        canPublish={canPublish}
        displayModal={displaySaveAndExitModal}
        hasNextStep={hasNextStep}
        onCancel={handleSaveAndExitModalCancel}
        onSubmit={handleSaveAndExitModalSubmit}
        onClose={handleSaveAndExitModalClose}
      />

      {displaySFTotalMonthlyPriceWithFeeAcknowledgementModal && (
        <SFTotalMonthlyPriceWithFeeAcknowledgementModal
          onClose={() => setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal(false)}
          onSubmit={() => {
            if (!isFreemiumEnabled || shouldAutoActivateRef.current || isActiveProductTypePremium) {
              shouldAutoActivateRef.current = false;
              autoActivate();
              return;
            }

            if (isFreemiumEnabled) {
              onGoToNextStep();
              return;
            }

            onGoToNextStep();
          }}
          isSubmitLoading={isActivateListingPending}
        />
      )}

      {displayListingPreviewModal && (
        <ListingPreviewModal onClose={() => setDisplayListingPreviewModal(false)} onSubmit={handleNextClick} />
      )}

      {activateListingError && (
        <ErrorModal onClose={() => setActivateListingError(null)} error={activateListingError} />
      )}
    </PageWizard>
  );
};

export default Review;
